/* eslint-disable no-use-before-define */
/* eslint-disable no-shadow */
import React, { useEffect, useMemo, useState, forwardRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import moment from 'moment';
import Datepicker from 'react-datepicker';
import { withRouter } from 'react-router-dom';

import EventPostScorePopup from '../../../../../components/PopUps/EventPostScorePopup';
import MatchSheetCard from './MatchSheetCard';

import { isEventOfficialHsEvent, isOrganizer } from '../../../../../utils/events';
import { createTypeByIdDict, updateCategoryTypesName } from '../../../../../utils/formatting';
import { normalizeMatchPositionsForPostScore } from '../../../../../utils/postScore';
import {
    sortMatchesWithLocation,
    filterMatchesByDate,
    filterMatchesByFacility,
    filterMatchesByDraw,
} from '../../../../../utils/matchSheetUtils';
import * as eventProfileActions from '../../../../../actions/event-profile-actions';
import { UNPUBLISHED_DRAWS_SCHEDULE_WARNING } from '../../../../../../constants/events';

import styles from './scheduleTab.scss';

const dayFmt = 'YYYY-MM-DD';

const formatDayLabel = (dayStr, eventTimeZone) => {
    if (!dayStr) {
        return 'Select Date';
    }
    if (eventTimeZone) {
        return moment.tz(dayStr, dayFmt, eventTimeZone).format('MMM D, YYYY');
    }
    return moment(dayStr, dayFmt).format('MMM D, YYYY');
};

const DatepickerInput = forwardRef(({ selectedDayStr = '', onClick = () => {}, eventTimeZone = null }, ref) => (
    <div className={`${styles.customdatepickerinput}`} onClick={onClick} ref={ref}>
        <span>{formatDayLabel(selectedDayStr, eventTimeZone)}</span>
        <img src="/img/icons/datepicker-icon.svg" alt="calendar icon" data-testid="match-sheet.datepicker-input.calendar-icon" />
    </div>
));

const MatchSheetV2 = (props) => {
    const auth = useSelector(({ auth }) => auth);
    const coreUI = useSelector(({ coreUI }) => coreUI);
    const { event, matchSheet, locations, draws } = useSelector(({ eventProfile }) => {
        const { event, matchSheet, locations, draws } = eventProfile;
        return { event, matchSheet, locations, draws };
    });

    const dispatch = useDispatch();

    const { drawsDict } = draws;

    const drawCategoryTypesDict = useMemo(() => {
        const { drawCategoryTypes = [] } = coreUI.types?.tms?.draw || {};
        return updateCategoryTypesName(createTypeByIdDict(drawCategoryTypes));
    }, [coreUI.types]);

    const [facility, setFacility] = useState('');
    const [drawId, setDrawId] = useState('');
    const [showDrawsDropDown, setShowDrawsDropDown] = useState(false);
    const [postScore, setPostScore] = useState({ showModal: false });

    const eventStartUtc = event.data?.eventSchedule?.eventStartUtc;
    const eventEndUtc = event.data?.eventSchedule?.eventEndUtc;
    const maxPostScoreDate = event.data?.eventSchedule.maxPostScoreDateUtc;
    const eventTimeZone = event.data?.eventSchedule.timeZone.value;

    // TZ helpers
    const toEventTz = (m) => (eventTimeZone ? m.tz(eventTimeZone) : m);
    const dayMomentInEvent = (dayStr) => (eventTimeZone ? moment.tz(dayStr, dayFmt, eventTimeZone) : moment(dayStr, dayFmt));

    // Default selected day in event TZ ( 'YYYY-MM-DD')
    const nowEvent = toEventTz(moment());
    const startLocal = eventStartUtc ? toEventTz(moment.utc(eventStartUtc)) : null;
    const endLocal = eventEndUtc ? toEventTz(moment.utc(eventEndUtc)) : null;
    const isNowOutsideEventDates = (startLocal && nowEvent.isBefore(startLocal)) || (endLocal && nowEvent.isAfter(endLocal));
    const defaultDay = isNowOutsideEventDates && startLocal ? startLocal.clone().format(dayFmt) : nowEvent.format(dayFmt);
    const [selectedDay, setSelectedDay] = useState(defaultDay);
    const isOfficialHSEvent = isEventOfficialHsEvent(event?.data);
    const isCurrentMemberAdmin = event.data && isOrganizer(event.data);
    const postScoreEnabled = !!maxPostScoreDate && moment.utc().isBefore(moment(maxPostScoreDate));

    const { showRoundColumn, showUnpublishedDraws } = props;

    const facilityIdsWithMatches = useMemo(
        () => new Set((matchSheet.matches ?? []).map((m) => m.facilityId)),
        [matchSheet.matches],
    );

    const roundSizeByDrawRound = useMemo(() => {
        const counts = {};
        (matchSheet.matches ?? []).forEach((m) => {
            const key = `${m.drawId}-${m.roundNumber}`;
            counts[key] = (counts[key] ?? 0) + 1;
        });
        return counts;
    }, [matchSheet.matches]);

    const getRoundSize = (match) => roundSizeByDrawRound[`${match.drawId}-${match.roundNumber}`] ?? 0;

    useEffect(() => {
        if (draws.data) {
            const visibleDraws = draws.data.filter(d => d.published || showUnpublishedDraws);
            if (visibleDraws.length > 0) {
                setShowDrawsDropDown(true);
            } else {
                setShowDrawsDropDown(false);
            }
        }
    }, [draws.data, showUnpublishedDraws]);

    const printSchedule = () => {
        props.history.push(`/events/${event.data.id}/matchsheet`);
    };

    const matchesToShow = useMemo(() => {
        if (!matchSheet.matches?.length) {
            return [];
        }
        const sortedMatchesOnSheet = sortMatchesWithLocation(matchSheet.matches, drawsDict);
        const visibleDrawMatches = showUnpublishedDraws
            ? sortedMatchesOnSheet
            : sortedMatchesOnSheet.filter(match => drawsDict[match.drawId]?.published);
        let filteredMatchSheet = filterMatchesByDate(visibleDrawMatches, selectedDay, eventTimeZone);
        if (facility !== '') {
            filteredMatchSheet = filterMatchesByFacility(filteredMatchSheet, facility);
        }
        if (drawId !== '') {
            filteredMatchSheet = filterMatchesByDraw(filteredMatchSheet, drawId, drawsDict);
        }
        return filteredMatchSheet;
    }, [matchSheet.matches, selectedDay, facility, drawId, drawsDict, eventTimeZone, showUnpublishedDraws]);

    const hasUnpublishedMatches = useMemo(
        () => matchesToShow.some(match => drawsDict[match.drawId]?.published === false),
        [matchesToShow, drawsDict],
    );

    const handleDateChange = (picked) => {
        const asMoment = moment.isMoment(picked) ? picked : moment(picked);
        setSelectedDay(toEventTz(asMoment).format(dayFmt));
    };

    const handleFacilityChange = (e) => {
        setFacility(e.target.value);
    };

    const handleDrawChange = (e) => {
        setDrawId(e.target.value);
    };

    const onPostScoreClick = (i) => {
        setPostScore({
            showModal: true,
            matchIndex: i,
        });
    };

    const menuOptions = [];
    if (isCurrentMemberAdmin && postScoreEnabled) {
        menuOptions.push({
            id: 'edit_score',
            label: 'Edit Score',
            value: 'edit_score',
        });
        menuOptions.push({
            id: 'delete_score',
            label: 'Delete Score',
            value: 'delete_score',
        });
    }

    const handleClearScore = async (matchIndex) => {
        try {
            const match = matchesToShow[matchIndex];
            await dispatch(eventProfileActions.clearScore(match, match.drawId));
        } catch (err) {
            console.error('Failed to clear match score', err);
        }
    };

    const onMenuSelected = (menuOption, matchIndex) => {
        switch (menuOption.id) {
            case 'edit_score':
                setPostScore({
                    showModal: true,
                    matchIndex,
                });
                break;
            case 'delete_score':
                handleClearScore(matchIndex);
                break;
            default:
                break;
        }
    };

    if (draws.loading || matchSheet.loading || locations.loading) {
        return (
            <div className="row">
                <div className="col-md-8">
                    <h6 className="" data-testid="match-sheet.loading.draws-title">Draws</h6>
                    <p data-testid="match-sheet.loading.loading-msg">Loading Match Sheet...</p>
                </div>
            </div>
        );
    }

    if (draws.error || matchSheet.error || locations.error) {
        return (
            <div className="row">
                <div className="col-md-8">
                    <h6 className="" data-testid="match-sheet.error.draws-title">Draws</h6>
                    <p data-testid="match-sheet.error.error-msg">Error Loading Match Sheet...</p>
                </div>
            </div>
        );
    }

    if (draws.data && matchSheet.matches && locations.data) {
        return (
            <>
                <div className="row mt16">
                    <div className="col-md-8 d-flex">
                        <div className="form-inline">
                            <div className={`form-group form-group-inline`}>
                                <div className="input-wrapper mr8">
                                    <Datepicker
                                        className="form-control form-control-sm"
                                        selected={dayMomentInEvent(selectedDay)}
                                        onChange={handleDateChange}
                                        minDate={startLocal ? startLocal.clone().startOf('day') : null}
                                        maxDate={endLocal ? endLocal.clone().endOf('day') : null}
                                        customInput={<DatepickerInput selectedDayStr={selectedDay} eventTimeZone={eventTimeZone} />}
                                        dateFormat="YYYY-MM-DD"
                                    />
                                </div>
                                <div className="input-wrapper mr8">
                                    <select
                                        className={`${styles.customFacilitiesSelectMs}`}
                                        value={facility}
                                        onChange={handleFacilityChange}
                                        data-testid="match-sheet.filters.facility-select"
                                    >
                                        <option value={''}>All Facilities</option>
                                        {locations.data?.map(eventLocation => eventLocation.facilities.map(locationFacility => {
                                            if (facilityIdsWithMatches.has(locationFacility.id)) {
                                                return <option key={locationFacility.id} value={locationFacility.name}>{locationFacility.name}</option>;
                                            }
                                            return null;
                                        }))}
                                    </select>
                                    <div className={`${styles.customDropdownIcon}`}>
                                        <img
                                            src="/img/icons/dropdown-icon.svg"
                                            alt="dropdown icon"
                                            style={{ width: '10px', height: '5px' }}
                                            data-testid="match-sheet.filters.dropdown-icon"
                                        />
                                    </div>
                                </div>
                                {showDrawsDropDown && (
                                    <div className="input-wrapper mr8">
                                        <select
                                            className={`${styles.allDrawsBorderStyle}`}
                                            value={drawId}
                                            onChange={handleDrawChange}
                                            data-testid="match-sheet.filters.draw-select"
                                        >
                                            {draws.data?.length && (
                                                <>
                                                    <option value={''}>All Draws</option>
                                                    {draws.data.filter(draw => draw.published || showUnpublishedDraws).map(eventDraw => {
                                                        return <option key={eventDraw.id} value={eventDraw.id}>{eventDraw.name}</option>;
                                                    })}
                                                </>
                                            )}
                                        </select>
                                        <div className={`${styles.customDropdownIcon}`}>
                                            <img
                                                src="/img/icons/dropdown-icon.svg"
                                                alt="dropdown icon"
                                                style={{ width: '10px', height: '5px' }}
                                                data-testid="match-sheet.filters.draw-dropdown-icon"
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="col-md-4 d-none d-md-flex aic jce">
                        {auth?.user && !props.hidePrintButton && (
                            <button
                                type="button"
                                className={`${styles.customPrintBtn} btn btn-small btn-default float-right `}
                                onClick={printSchedule}
                                data-testid="match-sheet.header.print-btn"
                            >
                                Print
                            </button>
                        )}
                    </div>
                </div>
                {showUnpublishedDraws && hasUnpublishedMatches && (
                    <div
                        className="alert alert-warning mt16"
                        role="alert"
                        data-testid="match-sheet.preview.unpublished-warning"
                    >
                        {UNPUBLISHED_DRAWS_SCHEDULE_WARNING}
                    </div>
                )}
                {matchesToShow.length > 0 && (
                    <div className="d-none d-md-flex text-small text-medium">
                        <div className="flex-grow p8 mt16 mb-2">
                            <div className="d-flex aic">
                                <div className={styles.msRow}>
                                    <div className={`${styles.msCol} ${styles.msColTime}`} data-testid="match-sheet.column-header.match-time">
                                        Match Time
                                    </div>
                                    <div className={`${styles.msCol} ${styles.msColLocation}`} data-testid="match-sheet.column-header.location">
                                        Location
                                    </div>
                                    <div className={`${styles.msCol} ${styles.msColDraw}`} data-testid="match-sheet.column-header.draw">
                                        Draw
                                    </div>
                                    {showRoundColumn && (
                                        <div className={`${styles.msCol} ${styles.msColRound}`} data-testid="match-sheet.column-header.round">
                                            Round
                                        </div>
                                    )}
                                    <div className={`${styles.msCol} ${styles.msColPlayer} text-right`} data-testid="match-sheet.column-header.player-team-a">
                                        Player/Team A
                                    </div>
                                    <div className={`${styles.msCol} ${styles.msColPlayer} text-left`} data-testid="match-sheet.column-header.player-team-b">
                                        Player/Team B
                                    </div>
                                    <div className={`${styles.msCol} ${styles.msColResult}`} data-testid="match-sheet.column-header.match-result">
                                        Match Result
                                    </div>
                                </div>
                                <div className={styles.optionsContainer} />
                            </div>
                        </div>
                    </div>
                )}
                {matchesToShow.length > 0 && (
                    matchesToShow.map((match, i) => {
                        return (
                            <MatchSheetCard
                                key={match.id}
                                match={match}
                                i={i}
                                eventTimeZone={eventTimeZone}
                                hidePrintButton={props.hidePrintButton}
                                menuOptions={menuOptions}
                                onMenuSelected={onMenuSelected}
                                isOfficialHSEvent={isOfficialHSEvent}
                                isCurrentMemberAdmin={isCurrentMemberAdmin}
                                postScoreEnabled={postScoreEnabled}
                                onPostScoreClick={onPostScoreClick}
                                sportTypeId={event.data?.sportTypeId}
                                drawCategoryTypesDict={drawCategoryTypesDict}
                                showRoundColumn={showRoundColumn}
                                roundSize={getRoundSize(match)}
                            />
                        );
                    })
                )}
                {postScore.showModal && (() => {
                    const matchesForPostScore = matchesToShow.map(normalizeMatchPositionsForPostScore);
                    return (
                        <EventPostScorePopup
                            event={event.data}
                            drawsDict={drawsDict}
                            draw={drawsDict[matchesForPostScore[postScore.matchIndex]?.drawId]}
                            matchIndex={postScore.matchIndex}
                            matches={matchesForPostScore}
                            onCloseClick={() => setPostScore({ showModal: false })}
                            postScoreAction={eventProfileActions.postScore}
                        />
                    );
                })()}
            </>
        );
    }
    return null;
};

MatchSheetV2.propTypes = {
    history: PropTypes.shape({
        push: PropTypes.func.isRequired,
    }).isRequired,
    hidePrintButton: PropTypes.bool,
    showRoundColumn: PropTypes.bool,
    showUnpublishedDraws: PropTypes.bool,
};

MatchSheetV2.defaultProps = {
    hidePrintButton: false,
    showRoundColumn: false,
    showUnpublishedDraws: false,
};

export default withRouter(MatchSheetV2);
